/**
 * The DPS preview's toolbar subset (08-PRV §5.1): free-text search (well / API / slot
 * id), phase filter, the future-locations-only toggle (default ON), the current zoom
 * window label, and the truthful "N of M shown" readout. Bound to the form-scoped
 * preview atoms (not the eval dialog's), so the preview's filter state is genuinely
 * ephemeral to this form. Per-unit expand is via the group chevrons.
 */

import { useAtom } from 'jotai';
import { Dropdown, Search, Toggle } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import {
  PHASE_LABELS,
  PHASE_ORDER,
  type PhaseKey,
} from 'pages/packages/show/eval3-new/schedule-dialog';
import {
  previewFutureOnlyAtom,
  previewPhaseFilterAtom,
  previewSearchAtom,
} from './schedule-preview-atoms';
import styles from './schedule-preview-toolbar.module.scss';

interface PhaseOption {
  id: PhaseKey | 'all';
  label: string;
}

const PHASE_OPTIONS: PhaseOption[] = [
  { id: 'all', label: 'All phases' },
  ...PHASE_ORDER.map((phase) => ({ id: phase, label: PHASE_LABELS[phase] })),
];

export interface SchedulePreviewToolbarProps {
  windowLabel: string;
  shownRowCount: number;
  totalRowCount: number;
}

/**
 * Renders the preview toolbar row.
 *
 * @param {SchedulePreviewToolbarProps} props - The window label and the shown/total counts.
 * @return {JSX.Element} The toolbar.
 */
export function SchedulePreviewToolbar({
  windowLabel,
  shownRowCount,
  totalRowCount,
}: SchedulePreviewToolbarProps): JSX.Element {
  const [search, setSearch] = useAtom(previewSearchAtom);
  const [phaseFilter, setPhaseFilter] = useAtom(previewPhaseFilterAtom);
  const [futureOnly, setFutureOnly] = useAtom(previewFutureOnlyAtom);

  const selectedPhase =
    PHASE_OPTIONS.find((option) => option.id === (phaseFilter ?? 'all')) ??
    PHASE_OPTIONS[0];

  return (
    <div className={styles.toolbar}>
      <Search
        size="sm"
        className={styles.search}
        labelText="Search wells"
        placeholder="Search well, API, or slot id"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <Dropdown<PhaseOption>
        id="preview-phase-filter"
        className={styles.phase}
        size="sm"
        label="Phase"
        titleText="Phase"
        hideLabel
        items={PHASE_OPTIONS}
        selectedItem={selectedPhase}
        itemToString={(item) => item?.label ?? ''}
        onChange={({ selectedItem }) =>
          setPhaseFilter(
            !selectedItem || selectedItem.id === 'all'
              ? null
              : (selectedItem.id as PhaseKey)
          )
        }
      />

      <Toggle
        id="preview-future-only"
        size="sm"
        className={styles.futureToggle}
        labelText="Future locations only"
        labelA="Off"
        labelB="On"
        toggled={futureOnly}
        onToggle={(checked) => setFutureOnly(checked)}
      />

      <span className={styles.window}>{windowLabel}</span>
      <span className={styles.shown} aria-live="polite">
        <FormattedCount value={shownRowCount} /> of{' '}
        <FormattedCount value={totalRowCount} /> shown
      </span>
    </div>
  );
}
